import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../ui/dialog";
import { Button } from "../ui/button";
import ConfirmDeleteUserDialog from "../../components/ModaleUser/ConfirmDeleteUserDialog";
import { useToast } from "../ui/toast";
import {
  useToggleAdminStatusMutation,
  useGetUserQuery,
} from "../../Services/modules/users";
import LoadingState from "../generic/LoadingState";
import ErrorState from "../generic/ErrorState";
import ViewSponsorsDialog from "./ViewSponsorsDialog";
// import AddResourcesDialog from "./AddResourcesDialog";
// import AddBoostsDialog from "./AddBoostDialog";
// import AddChestsDialog from "./AddChestsDialog";

export default function UserActionsDialog({
  user,
  open,
  onOpenChange,
  queryArgs,
}) {
  // États des modales séparés pour éviter les problèmes de dépendances
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sponsorsModalOpen, setSponsorsModalOpen] = useState(false);
  const [resourcesModalOpen, setResourcesModalOpen] = useState(false);
  const [boostsModalOpen, setBoostsModalOpen] = useState(false);
  const [chestsModalOpen, setChestsModalOpen] = useState(false);

  const [error, setError] = useState("");
  const { addToast } = useToast();

  const {
    data: fullUser,
    isLoading: isLoadingUser,
    error: userError,
    refetch: refetchUser,
    isFetching: isFetchingUser,
  } = useGetUserQuery({ uid: user?.uid }, { refetchOnMountOrArgChange: true });

  const [toggleAdminStatus, { isLoading: isTogglingAdmin }] =
    useToggleAdminStatusMutation();

  const resetModals = useCallback(() => {
    setDeleteModalOpen(false);
    setSponsorsModalOpen(false);
    setResourcesModalOpen(false);
    setBoostsModalOpen(false);
    setChestsModalOpen(false);
    setError("");
  }, []);

  useEffect(() => {
    if (!open) resetModals();
  }, [open, resetModals]);

  useEffect(() => {
    resetModals();
  }, [user?.uid, resetModals]);

  const hasUserError = useMemo(() => !!userError, [userError]);
  const userErrorMessage = useMemo(
    () =>
      userError?.data?.error || userError?.error || "Utilisateur non trouvé",
    [userError]
  );
  const isAdmin = useMemo(
    () => !hasUserError && fullUser?.admin !== undefined && fullUser?.admin,
    [fullUser, hasUserError]
  );
  const adminStatus = useMemo(() => {
    if (isLoadingUser) return "Chargement...";
    if (hasUserError) return userErrorMessage;
    return isAdmin ? "Admin" : "Non admin";
  }, [isLoadingUser, hasUserError, isAdmin, userErrorMessage]);
  const nextAdminStatus = useMemo(
    () => (isAdmin ? "Non admin" : "Admin"),
    [isAdmin]
  );

  const name = useMemo(() => {
    if (hasUserError) {
      return user?.firstName && user?.lastName
        ? `${user?.firstName} ${user?.lastName}`
        : user?.email || "Utilisateur";
    }
    if (fullUser?.firstName && fullUser?.lastName) {
      return `${fullUser?.firstName} ${fullUser?.lastName}`;
    }
    return fullUser?.email || user?.email || "Utilisateur";
  }, [fullUser, hasUserError, user]);

  const displayEmail = useMemo(
    () =>
      hasUserError
        ? user?.email || "Email non disponible"
        : fullUser?.email || user?.email || "Email non disponible",
    [fullUser, hasUserError, user]
  );

  const actionsDisabled = useMemo(
    () => hasUserError || isLoadingUser || isFetchingUser,
    [hasUserError, isLoadingUser, isFetchingUser]
  );
  const checkBoxDisabled = useMemo(
    () => actionsDisabled || isTogglingAdmin,
    [actionsDisabled, isTogglingAdmin]
  );
  const labelTextColor = useMemo(
    () =>
      actionsDisabled || isTogglingAdmin ? "text-gray-400" : "text-gray-900",
    [actionsDisabled, isTogglingAdmin]
  );

  const handleToggleAdmin = useCallback(async () => {
    if (actionsDisabled) return;
    setError("");
    try {
      await toggleAdminStatus({ uid: user?.uid }).unwrap();
      addToast(
        `${name} ${!isAdmin ? "est maintenant admin" : "n'est plus admin"}`,
        "success"
      );
      await refetchUser();
    } catch (err) {
      addToast(error || "Erreur lors du changement de statut", "error");
    }
  }, [
    actionsDisabled,
    toggleAdminStatus,
    user?.uid,
    addToast,
    name,
    isAdmin,
    refetchUser,
    error,
  ]);

  // Handlers des actions mémorisés sans dépendances problématiques
  const handleViewSponsors = useCallback(() => {
    if (actionsDisabled) return;
    setSponsorsModalOpen(true);
  }, [actionsDisabled]);

  const handleAddResources = useCallback(() => {
    if (actionsDisabled) return;
    setResourcesModalOpen(true);
  }, [actionsDisabled]);

  const handleAddBoosts = useCallback(() => {
    if (actionsDisabled) return;
    setBoostsModalOpen(true);
  }, [actionsDisabled]);

  const handleAddChests = useCallback(() => {
    if (actionsDisabled) return;
    setChestsModalOpen(true);
  }, [actionsDisabled]);

  const handleDelete = useCallback(() => {
    if (actionsDisabled) return;
    setDeleteModalOpen(true);
  }, [actionsDisabled]);

  const successHandlers = {
    resources: useCallback(
      () => addToast("Ressources ajoutées avec succès", "success"),
      [addToast]
    ),
    boosts: useCallback(
      () => addToast("Boosts ajoutés avec succès", "success"),
      [addToast]
    ),
    chests: useCallback(
      () => addToast("Coffres ajoutés avec succès", "success"),
      [addToast]
    ),
    delete: useCallback(
      (user, result) => {
        resetModals();
        onOpenChange(false);
        addToast(
          `${user?.firstName} ${user?.lastName} a été supprimé`,
          "success"
        );
      },
      [resetModals, onOpenChange, addToast]
    ),
  };

  const actions = useMemo(
    () => [
      {
        key: "viewSponsors",
        label: "Voir le réseau de parrainage",
        handler: handleViewSponsors,
        variant: "outline",
      },
      // {
      //   key: "addBoosts",
      //   label: "Ajouter des boosts",
      //   handler: handleAddBoosts,
      //   variant: "outline",
      // },
      // {
      //   key: "addChests",
      //   label: "Ajouter des coffres",
      //   handler: handleAddChests,
      //   variant: "outline",
      // },
      // {
      //   key: "addResources",
      //   label: "Ajouter des ressources",
      //   handler: handleAddResources,
      //   variant: "outline",
      // },
      {
        key: "delete",
        label: "Supprimer l'utilisateur",
        handler: handleDelete,
        variant: "destructive",
      },
    ],
    [
      handleViewSponsors,
      handleAddBoosts,
      handleAddChests,
      handleAddResources,
      handleDelete,
    ]
  );

  const dialogContent = useMemo(() => {
    if (isLoadingUser) {
      return <LoadingState text="Chargement des informations utilisateur..." />;
    }

    if (hasUserError) {
      return (
        <ErrorState
          error={userErrorMessage}
          title="Erreur utilisateur"
          className="py-8 text-center"
        />
      );
    }

    if (!user) return null;

    return (
      <>
        {error && (
          <div className="p-3 text-sm text-red-500 bg-red-50 rounded">
            {error}
          </div>
        )}

        <div className="my-4 space-y-4">
          {/* Admin checkbox */}
          <div className="flex items-center p-3 space-x-3 rounded border">
            <input
              type="checkbox"
              id="admin-status"
              checked={isAdmin}
              onChange={handleToggleAdmin}
              disabled={checkBoxDisabled}
              className="w-4 h-4 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 focus:ring-2"
            />
            <label
              htmlFor="admin-status"
              className={`flex-1 mb-0 text-sm font-medium cursor-pointer ${labelTextColor}`}
            >
              Passer en {nextAdminStatus}
            </label>
          </div>

          {/* Action buttons */}
          {actions.map(({ key, label, handler, variant }) => (
            <Button
              key={key}
              variant={variant}
              className="w-full"
              onClick={handler}
              disabled={actionsDisabled}
            >
              {label}
            </Button>
          ))}
        </div>
      </>
    );
  }, [
    isLoadingUser,
    hasUserError,
    user,
    error,
    isAdmin,
    handleToggleAdmin,
    checkBoxDisabled,
    labelTextColor,
    nextAdminStatus,
    actions,
    userErrorMessage,
    actionsDisabled,
  ]);

  if (!user) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Actions sur {name}</DialogTitle>
            <DialogDescription>
              Email : {displayEmail}
              {fullUser?.admin && !hasUserError && (
                <span className="px-2 py-1 ml-2 text-xs text-blue-800 bg-blue-100 rounded-full">
                  {adminStatus}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {dialogContent}

          <DialogFooter>
            <DialogClose asChild>
              {/* <Button variant="secondary">Fermer</Button> */}
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {!hasUserError && (
        <>
          <ConfirmDeleteUserDialog
            user={user}
            open={deleteModalOpen}
            onOpenChange={setDeleteModalOpen}
            onSuccess={successHandlers.delete}
            queryArgs={queryArgs}
          />

          <ViewSponsorsDialog
            user={user}
            open={sponsorsModalOpen}
            onOpenChange={setSponsorsModalOpen}
          />

          {/* <AddResourcesDialog
            user={user}
            open={resourcesModalOpen}
            onOpenChange={setResourcesModalOpen}
            onSuccess={successHandlers.resources}
          /> */}

          {/* <AddBoostsDialog
            user={user}
            open={boostsModalOpen}
            onOpenChange={setBoostsModalOpen}
            onSuccess={successHandlers.boosts}
          /> */}

          {/* <AddChestsDialog
            user={user}
            open={chestsModalOpen}
            onOpenChange={setChestsModalOpen}
            onSuccess={successHandlers.chests}
          /> */}
        </>
      )}
    </>
  );
}
